import { z } from 'zod';
/**
 * Third-Party Risk register — the curated, persistent layer of the risk
 * assessment. The integration INVENTORY is derived from code by the weekly job
 * (never hand-maintained); this module types the repos, the derived inventory
 * rows, and the risk findings (the unit of action Jay turns into a ticket).
 *
 * Scoring: each finding carries a severity; an integration's score is the
 * weighted worst of its OPEN findings (0–100); a repo's score is the worst of
 * its integrations; priority = score × repo.importance. All computed, not
 * stored by hand — see apps/api/src/modules/risk/scoring.ts.
 */
export const RiskTier = z.enum(['low', 'medium', 'high']);
export const RiskFindingType = z.enum(['deprecation', 'security', 'upgrade', 'auth_deprecation']);
export const RiskFindingSeverity = z.enum(['low', 'medium', 'high']);
/** Remediation loop: open → triaged → ticketed → done. */
export const RiskFindingStatus = z.enum(['open', 'triaged', 'ticketed', 'done']);
/**
 * The two monitoring types in the risk section:
 *   - "provider"      — third-party provider risk: the APIs/SDKs a repo depends
 *     on (deprecation, security, upgrade, auth deprecation).
 *   - "repo_security" — repository security risk: the repo's own posture
 *     (dependency vulnerabilities, outdated runtimes, exposed secrets).
 */
export const RiskIntegrationKind = z.enum(['provider', 'repo_security']);
// ─── Repo ─────────────────────────────────────────────────────────────────────
export const RiskRepo = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    // Human-set importance weight (1–10) that turns raw risk into priority.
    importance: z.number().int().min(1).max(10),
    lastScannedAt: z.string().nullable(),
    // Derived rollups (present on reads): worst integration score + counts.
    score: z.number().optional(), // 0–100, worst open integration score
    tier: RiskTier.optional(),
    priority: z.number().optional(), // score × importance (remediation rank)
    integrationCount: z.number().optional(),
    openFindingCount: z.number().optional(),
    highFindingCount: z.number().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateRiskRepoInput = z.object({
    slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/, 'slug: lowercase letters, digits, hyphens'),
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    importance: z.number().int().min(1).max(10).optional(),
});
export const UpdateRiskRepoInput = CreateRiskRepoInput.partial().extend({
    description: z.string().max(500).nullable().optional(),
});
// ─── Finding (the unit of action) ─────────────────────────────────────────────
// Declared before RiskIntegration because an integration's read carries its
// open findings.
export const RiskFinding = z.object({
    id: z.string(),
    integrationId: z.string(),
    type: RiskFindingType,
    severity: RiskFindingSeverity,
    title: z.string(),
    detail: z.string().nullable(),
    source: z.string().nullable(),
    status: RiskFindingStatus,
    ticketId: z.string().nullable(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ─── Integration (derived inventory) ──────────────────────────────────────────
export const RiskIntegration = z.object({
    id: z.string(),
    repoId: z.string(),
    // Monitoring type: provider (third-party API/SDK) or repo_security (the
    // repo's own posture). Pre-existing rows are "provider".
    kind: RiskIntegrationKind,
    provider: z.string(),
    providerKey: z.string(),
    category: z.string().nullable(),
    version: z.string().nullable(),
    endpoint: z.string().nullable(),
    source: z.string().nullable(),
    score: z.number().int().min(0).max(100),
    tier: RiskTier,
    lastCheckedAt: z.string().nullable(),
    // Derived: the open findings on this integration (present on detail reads).
    findings: z.array(RiskFinding).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateRiskFindingInput = z.object({
    integrationId: z.string(),
    type: RiskFindingType,
    severity: RiskFindingSeverity.optional(),
    title: z.string().min(1).max(300),
    detail: z.string().max(4000).optional(),
    source: z.string().max(500).optional(),
    status: RiskFindingStatus.optional(),
});
export const UpdateRiskFindingInput = z.object({
    type: RiskFindingType.optional(),
    severity: RiskFindingSeverity.optional(),
    title: z.string().min(1).max(300).optional(),
    detail: z.string().max(4000).nullable().optional(),
    source: z.string().max(500).nullable().optional(),
    status: RiskFindingStatus.optional(),
    // Full-replace: link the cleanup ticket (id or code), or null to clear.
    ticketId: z.string().nullable().optional(),
});
// ─── Agent snapshot (weekly job writes the derived inventory) ─────────────────
// The weekly scanner re-derives each repo's integrations + findings from code
// and posts this snapshot. It is idempotent: integrations upsert on
// (repoSlug, providerKey); findings upsert on fingerprint and their status is
// PRESERVED (a re-scan never re-opens a finding Jay marked done/ticketed).
export const SnapshotIntegration = z.object({
    // Monitoring type for this inventory row. Omit for "provider" (the
    // third-party API/SDK inventory). The repo-security scan posts one
    // "repo_security" row per repo (providerKey "repo-security").
    kind: RiskIntegrationKind.optional(),
    provider: z.string().min(1).max(160),
    providerKey: z.string().min(1).max(120).regex(/^[a-z0-9_-]+$/),
    category: z.string().max(80).optional(),
    version: z.string().max(200).optional(),
    endpoint: z.string().max(400).optional(),
    source: z.string().max(40).optional(),
});
export const SnapshotFinding = z.object({
    // Which integration this finding belongs to (providerKey within the repo).
    providerKey: z.string().min(1).max(120),
    type: RiskFindingType,
    severity: RiskFindingSeverity,
    title: z.string().min(1).max(300),
    detail: z.string().max(4000).optional(),
    source: z.string().max(500).optional(),
});
export const RiskRepoSnapshot = z.object({
    slug: z.string().min(1).max(120),
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    // Omit to leave the human-set importance unchanged.
    importance: z.number().int().min(1).max(10).optional(),
    integrations: z.array(SnapshotIntegration).default([]),
    findings: z.array(SnapshotFinding).default([]),
});
export const RiskSnapshotInput = z.object({
    repos: z.array(RiskRepoSnapshot).min(1),
    // Optional: a free-text digest summary to store alongside the scan.
    digest: z.string().max(20000).optional(),
});
// ─── Infrastructure scorecard (AWS security posture) ─────────────────────────
// The weekly job runs Prowler against the read-only IAM user, aggregates the
// resource-level ASFF findings to the CHECK level, and posts this snapshot.
// Idempotent: findings upsert on checkId and their status/ticketId are
// PRESERVED (a re-scan never re-opens a closed check). Each scan also appends
// an InfraScore history row (the trend line).
export const InfraSeverity = z.enum(['critical', 'high', 'medium', 'low']);
export const InfraFindingStatus = z.enum(['open', 'triaged', 'ticketed', 'done']);
export const InfraFinding = z.object({
    id: z.string(),
    checkId: z.string(),
    service: z.string(),
    category: z.string(),
    severity: InfraSeverity,
    title: z.string(),
    detail: z.string().nullable(),
    resourceCount: z.number().int().min(1),
    status: InfraFindingStatus,
    ticketId: z.string().nullable(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const InfraScore = z.object({
    id: z.string(),
    takenAt: z.string(),
    overall: z.number().int().min(0).max(100),
    checksTotal: z.number().int(),
    checksPass: z.number().int(),
    checksFail: z.number().int(),
    critical: z.number().int(),
    high: z.number().int(),
    medium: z.number().int(),
    low: z.number().int(),
});
// One failing check, as posted by the weekly job.
export const SnapshotInfraFinding = z.object({
    checkId: z.string().min(1).max(160),
    service: z.string().min(1).max(60),
    category: z.string().min(1).max(60),
    severity: InfraSeverity,
    title: z.string().min(1).max(300),
    detail: z.string().max(4000).optional(),
    resourceCount: z.number().int().min(1).max(100000).optional(),
});
export const InfraSnapshotInput = z.object({
    // 'full' (the periodic whole-account scan) or 'recheck' (a targeted
    // re-scan of one or a few checks after a fix). A recheck never appends a
    // score-history row (its score is scoped to a handful of checks and would
    // distort the trend) and auto-close is limited to its `scope`.
    kind: z.enum(['full', 'recheck']).default('full'),
    // The services this scan covered. The endpoint only auto-closes findings for
    // THESE services (a check not in the posted set is "now passing" only if its
    // service was actually scanned — otherwise a partial scan would wrongly close
    // findings from services it didn't touch).
    services: z.array(z.string().min(1).max(60)).default([]),
    // The exact checks this scan covered (a recheck lists just the re-checked
    // checkIds). When present, auto-close is limited to these checks — stricter
    // than the service-level guard, so a recheck of one check can never close
    // any other finding.
    scope: z.array(z.string().min(1).max(160)).default([]),
    // The requestedAt of the UI rescan/recheck request this scan fulfills (set
    // by the scan-box poller). When present and matching the stored request, the
    // endpoint clears it — so a late full scan never clears a NEWER request.
    rescanRequestId: z.string().optional(),
    // The computed score for this scan (weighted pass-rate + check counts).
    score: z.object({
        overall: z.number().int().min(0).max(100),
        checksTotal: z.number().int().min(0),
        checksPass: z.number().int().min(0),
        checksFail: z.number().int().min(0),
        critical: z.number().int().min(0),
        high: z.number().int().min(0),
        medium: z.number().int().min(0),
        low: z.number().int().min(0),
    }),
    // The failing checks (passing checks are not stored — only what needs fixing).
    findings: z.array(SnapshotInfraFinding).default([]),
    // Optional: the full Prowler ASFF output for the audit trail (capped).
    rawJson: z.string().max(20_000_000).optional(),
});
//# sourceMappingURL=risk.js.map